import { apiRequest } from './axiosConfig';
import { User, OnboardingStatusResponse, CompleteOnboardingResponse } from '../../types';

export interface UserUpdateRequest {
  name?: string;
  email?: string;
  password?: string;
  password_confirmation?: string;
}

export type TrialType = 'days' | 'usage';

export interface StartTrialResponse {
  success: boolean;
  message: string;
  trial_started_at?: string;
  trial_ends_at?: string;
  trial_type?: TrialType;
  trial_usage_limit?: number;
  trial_usage_count?: number;
}

export interface TrialStatusResponse {
  trial_started_at: string | null;
  trial_ends_at: string | null;
  trial_type: TrialType | null;
  trial_usage_count: number;
  trial_usage_limit: number | null;
  is_in_trial: boolean;
  has_trial_expired: boolean;
  has_usage_limit_reached: boolean;
  can_use_service: boolean;
}

class UserService {
  /**
   * ユーザー一覧を取得（管理者用）
   * @returns ユーザー一覧
   */
  async getAllUsers(): Promise<User[]> {
    try {
      return await apiRequest.get<User[]>('/users');
    } catch (error) {
      console.error('Failed to get all users:', error);
      throw error;
    }
  }

  /**
   * 特定のユーザー情報を取得
   * @param userId ユーザーID
   * @returns ユーザー情報
   */
  async getUserById(userId: number): Promise<User> {
    try {
      return await apiRequest.get<User>(`/users/${userId}`);
    } catch (error) {
      console.error(`Failed to get user with id ${userId}:`, error);
      throw error;
    }
  }

  /**
   * ユーザー情報を更新
   * @param userId ユーザーID
   * @param userData 更新するユーザー情報
   * @returns 更新後のユーザー情報
   */
  async updateUser(userId: number, userData: UserUpdateRequest): Promise<User> {
    try {
      return await apiRequest.put<User>(`/users/${userId}`, userData);
    } catch (error) {
      console.error(`Failed to update user with id ${userId}:`, error);
      throw error;
    }
  }

  /**
   * ユーザーを削除（管理者用）
   * @param userId ユーザーID
   * @returns 成功したかどうか
   */
  async deleteUser(userId: number): Promise<{ success: boolean, message: string }> {
    try {
      return await apiRequest.delete<{ success: boolean, message: string }>(`/users/${userId}`);
    } catch (error) {
      console.error(`Failed to delete user with id ${userId}:`, error);
      throw error;
    }
  }

  /**
   * ユーザーのサブスクリプションプランを更新
   * @param userId ユーザーID
   * @param plan サブスクリプションプラン名
   * @returns 更新後のユーザー情報
   */
  async updateSubscription(userId: number, plan: string): Promise<User> {
    try {
      return await apiRequest.patch<User>(`/users/${userId}/subscription`, { subscription_plan: plan });
    } catch (error) {
      console.error(`Failed to update subscription for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * ユーザーの使用統計情報を取得
   * @param userId ユーザーID
   * @returns 使用統計情報
   */
  async getUserStats(userId: number): Promise<{
    total_messages: number,
    total_tokens: number,
    total_chats: number,
    remaining_tokens: number
  }> {
    try {
      const tokenManager = (await import('../../utils/tokenManager')).default;
      // ユーザープロフィールを取得（統計情報を含む）
      const response = await apiRequest.get<User & {
        stats?: {
          total_messages: number,
          total_tokens: number,
          total_chats: number,
          remaining_tokens: number
        }
      }>(`/users/profile`);

      if (response.stats) {
        return response.stats;
      }

      return {
        total_messages: 0,
        total_tokens: 0,
        total_chats: 0,
        remaining_tokens: 10000
      };
    } catch (error) {
      console.error(`UserService: Failed to get stats for user ${userId}:`, error);
      console.error('UserService: Error details:', JSON.stringify(error, null, 2));
      throw error;
    }
  }

  /**
   * オンボーディング状態を取得
   * @returns オンボーディング状態
   */
  async getOnboardingStatus(): Promise<OnboardingStatusResponse> {
    try {
      return await apiRequest.get<OnboardingStatusResponse>('/users/onboarding-status');
    } catch (error) {
      console.error('Failed to get onboarding status:', error);
      throw error;
    }
  }

  /**
   * オンボーディングを完了としてマーク
   * @returns 完了レスポンス
   */
  async completeOnboarding(): Promise<CompleteOnboardingResponse> {
    try {
      return await apiRequest.post<CompleteOnboardingResponse>('/users/complete-onboarding');
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      throw error;
    }
  }

  /**
   * 無料トライアルを開始
   * @param trialType トライアルタイプ（'days': 7日間, 'usage': 10回）
   * @returns トライアル開始結果
   */
  async startTrial(trialType: TrialType): Promise<StartTrialResponse> {
    try {
      return await apiRequest.post<StartTrialResponse>('/users/start-trial', {
        trial_type: trialType,
      });
    } catch (error) {
      console.error('Failed to start trial:', error);
      throw error;
    }
  }

  /**
   * トライアルの状態を取得
   * @returns トライアル状態の詳細
   */
  async getTrialStatus(): Promise<TrialStatusResponse> {
    try {
      return await apiRequest.get<TrialStatusResponse>('/users/trial-status');
    } catch (error) {
      console.error('Failed to get trial status:', error);
      throw error;
    }
  }
}

export default new UserService();
